/**
 * Generated by orval v7.9.0 🍺
 * Do not edit manually.
 * Meeting BaaS API
 * API for managing meeting bots, calendar integrations, and webhooks
 * OpenAPI spec version: 2.0.0
 */

import type { BodyType } from "../../../../custom-axios"

import { customInstance } from "../../../../custom-axios"
import type {
  CreateZoomCredential201,
  CreateZoomCredentialBody,
  DeleteZoomCredential200,
  GetZoomCredential200,
  ListZoomCredentials200,
  ListZoomCredentialsParams,
  UpdateZoomCredential200,
  UpdateZoomCredentialBody
} from "../../schema"

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1]

/**
 * Create a new Zoom credential for your team.

    Zoom credentials store your Zoom OAuth App credentials (client_id and client_secret) securely encrypted. You can create two types of credentials:

    **App-only credentials:** Provide only `name`, `client_id`, and `client_secret`. These credentials can be used for SDK authentication when bots join meetings.

    **User-authorized credentials:** Additionally provide `authorization_code` and `redirect_uri`. The API will exchange the authorization code for OAuth tokens, enabling OBF (On-Behalf-Of) token support. OBF tokens allow bots to join meetings on behalf of a specific Zoom user. The authorising user's `zoom_email` and `zoom_display_name` are captured from Zoom's `/users/me` API at exchange time (requires the `user:read:user` scope) and returned in the response so you can show end users which Zoom account is connected.

    **Custom Metadata:** Pass an optional `extra` JSON object to tag the credential with your own key-value pairs (for example an internal user ID, environment, or tenant). The data is stored as-is, returned by all credential endpoints, and is filterable on the list endpoint via the `extra` query parameter.

    **Security:** All credentials are encrypted at rest using AES-256-GCM. Client secrets and OAuth tokens are never returned in API responses.

    **OAuth Flow:** To obtain an authorization code, redirect users to Zoom's OAuth authorization endpoint and capture the code from the callback. Ensure your redirect URI exactly matches the one registered in your Zoom OAuth App.

    **Error Scenarios:**
    - `400 Bad Request`: Invalid input or missing redirect_uri when authorization_code is provided
    - `400 Bad Request`: Failed to exchange authorization code (invalid code or redirect_uri mismatch)
 * @summary Create a Zoom credential
 */
export const createZoomCredential = (
  createZoomCredentialBody: BodyType<CreateZoomCredentialBody>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<CreateZoomCredential201>(
    {
      url: "/v2/zoom-credentials",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: createZoomCredentialBody
    },
    options
  )
}
/**
 * List all Zoom credentials for your team.

    Returns all non-deleted credentials with their metadata. Sensitive fields (client_secret, OAuth tokens) are never included in responses.

    **Response Fields:**
    - `credential_id`: UUID to reference this credential in bot requests
    - `name`: User-friendly name for identification
    - `credential_type`: "app" (SDK only) or "user" (with OAuth tokens)
    - `zoom_user_id`: The Zoom user ID (only for "user" type)
    - `zoom_email` / `zoom_display_name`: The authorising Zoom user's email and display name, captured from Zoom's `/users/me` API at OAuth time (only for "user" type, requires the `user:read:user` scope)
    - `state`: "active" or "invalid"
    - `last_error_message`: Last OBF token fetch error (if any)
    - `extra`: The optional user-supplied JSON metadata attached to the credential

    **Filtering:** Narrow the result set with optional query parameters (combined with AND):
    - `name`, `zoom_email`, `zoom_display_name`: case-insensitive partial match
    - `zoom_user_id`: exact match
    - `credential_type`, `state`: comma-separated enum lists (e.g. `credential_type=user`, `state=active,invalid`)
    - `extra`: `key:value` pairs against the `extra` JSON payload, comma-separated for multiple conditions (e.g. `extra=internal_user_id:u_42,environment:production`). Values are matched exactly (case-sensitive); credentials missing the key are excluded.

    **Error Tracking:** If bots fail to fetch OBF tokens using a credential, the error is recorded in `last_error_message` and `last_error_at`. These fields are cleared on successful OBF token fetch.
 * @summary List Zoom credentials
 */
export const listZoomCredentials = (
  params?: ListZoomCredentialsParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ListZoomCredentials200>(
    { url: "/v2/zoom-credentials", method: "GET", params },
    options
  )
}
/**
 * Get detailed information about a specific Zoom credential.

    Returns the credential's metadata including its current state and any recent errors. Sensitive fields (client_secret, OAuth tokens) are never included.

    For "user" type credentials, the response also includes `zoom_email` and `zoom_display_name` (captured from Zoom's `/users/me` API at OAuth time) and any `extra` JSON metadata you attached at creation or via `PATCH`.

    **Error Tracking:** The `last_error_message` and `last_error_at` fields show the most recent OBF token fetch failure. Check these fields if bots using this credential are failing to join meetings.

    Returns 404 if the credential is not found or does not belong to your team.
 * @summary Get a Zoom credential
 */
export const getZoomCredential = (id: string, options?: SecondParameter<typeof customInstance>) => {
  return customInstance<GetZoomCredential200>(
    { url: `/v2/zoom-credentials/${id}`, method: "GET" },
    options
  )
}
/**
 * Update an existing Zoom credential.

    You can update the credential name, SDK credentials (client_id/client_secret), the user-supplied `extra` metadata, or re-authorize with new OAuth tokens.

    **Updating Name:** Provide only `name` to rename the credential.

    **Updating SDK Credentials:** Provide both `client_id` and `client_secret` together to update the SDK credentials.

    **Updating Custom Metadata:** Provide `extra` to replace the credential's metadata payload, or send `"extra": null` to clear it.

    **Re-authorizing:** Provide `authorization_code`, `redirect_uri`, `client_id`, and `client_secret` to exchange a new authorization code for fresh OAuth tokens. This also refreshes `zoom_email` and `zoom_display_name` from Zoom's `/users/me` API, resets the credential state to "active", and clears any error messages.

    **Error Scenarios:**
    - `400 Bad Request`: Missing redirect_uri when authorization_code is provided
    - `400 Bad Request`: Failed to exchange authorization code
    - `404 Not Found`: Credential not found or does not belong to your team
 * @summary Update a Zoom credential
 */
export const updateZoomCredential = (
  id: string,
  updateZoomCredentialBody: BodyType<UpdateZoomCredentialBody>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<UpdateZoomCredential200>(
    {
      url: `/v2/zoom-credentials/${id}`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: updateZoomCredentialBody
    },
    options
  )
}
/**
 * Delete a Zoom credential (soft delete).

    The credential is marked as deleted and will no longer appear in list responses. Bots currently using this credential will fail to fetch OBF tokens.

    **Impact on Bots:** If bots are configured to use this credential (via `zoom_config.credential_id`), they will fail with an error when trying to fetch OBF tokens. Make sure to update any bot configurations before deleting a credential.

    **Soft Delete:** The credential is soft-deleted and can potentially be restored by support if needed. All associated encrypted data remains in the database.

    Returns 404 if the credential is not found or does not belong to your team.
 * @summary Delete a Zoom credential
 */
export const deleteZoomCredential = (
  id: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<DeleteZoomCredential200>(
    { url: `/v2/zoom-credentials/${id}`, method: "DELETE" },
    options
  )
}
export type CreateZoomCredentialResult = NonNullable<
  Awaited<ReturnType<typeof createZoomCredential>>
>
export type ListZoomCredentialsResult = NonNullable<Awaited<ReturnType<typeof listZoomCredentials>>>
export type GetZoomCredentialResult = NonNullable<Awaited<ReturnType<typeof getZoomCredential>>>
export type UpdateZoomCredentialResult = NonNullable<
  Awaited<ReturnType<typeof updateZoomCredential>>
>
export type DeleteZoomCredentialResult = NonNullable<
  Awaited<ReturnType<typeof deleteZoomCredential>>
>
